import { Suspense, useMemo, lazy, useEffect } from "react";
import { useAuth } from "../../providers/auth";
import { Routes, Route, Navigate } from "react-router-dom";

import { AuthState } from "../auth/types";

import { NoMatch } from "./NoMatch";
import { IntegrationErrorView } from "../../views/integration-error";
import { HubView } from "views/hub";
import { CreditMonitoringView } from "views/credit-monitoring";
import { useIntercom } from "react-use-intercom";
import { RentReportingIssuesVerifyRentView } from "views/rent-reporting-issues-verify-rent";
import { MoveOutFlow } from "views/rent-reporting-id-move-out";
import { RentReportingIssuesM2MLeaseEndView } from "views/rent-reporting-id-issues-m2m-lease-end";

// Lazy loading components
// Lazy loading components
const RentReportingOnboardingView = lazy(() =>
  import("views/rent-reporting-onboarding").then((module) => ({
    default: module.RentReportingOnboardingView
  }))
);
const RentReportingId = lazy(() =>
  import("views/rent-reporting-id").then((module) => ({
    default: module.RentReportingId
  }))
);
const SettingsView = lazy(() =>
  import("views/settings").then((module) => ({ default: module.SettingsView }))
);
const PlaidConnectView = lazy(() =>
  import("views/plaid-connect-view").then((module) => ({
    default: module.PlaidConnectView
  }))
);
const SettingsLinkedAccountsView = lazy(() =>
  import("views/settings-linked-accounts").then((module) => ({
    default: module.SettingsLinkedAccountsView
  }))
);
const SettingsDeleteAccountView = lazy(() =>
  import("views/settings-delete-account").then((module) => ({
    default: module.SettingsDeleteAccountView
  }))
);
const DevManualLogout = lazy(() =>
  import("views/_dev-manual-logout").then((module) => ({
    default: module.DevManualLogout
  }))
);

export const AppRouter = () => {
  const { authState, partner, token, sdkConfig } = useAuth();
  const intercom = useIntercom();
  const theme = useMemo(() => {
    const ret: Record<string, string> = {};
    if (partner?.colors?.primary && Object.keys(partner.colors.primary).length)
      Object.entries(partner.colors.primary).forEach(([key, value]) => {
        ret[`--primary-${key}`] = value;
      });

    if (partner?.base_border_radius)
      ret["--base-border-radius"] = partner.base_border_radius;

    if (partner?.colors?.background)
      Object.entries(partner.colors.background).forEach(([key, value]) => {
        ret[`--background-${key}`] = value;
      });

    if (partner?.font_family) {
      if (partner.font_family_link) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = partner?.font_family_link;
        if (partner.font_family_link.includes("fonts.googleapis.com")) {
          const preconnect = document.createElement("link");
          preconnect.rel = "preconnect";
          preconnect.href = "https://fonts.gstatic.com";
          document.head.appendChild(preconnect);
          const preconnect2 = document.createElement("link");
          preconnect2.rel = "preconnect";
          preconnect2.href = "https://fonts.googleapis.com";
          preconnect2.crossOrigin = "anonymous";
          document.head.appendChild(preconnect2);
        }
        document.head.appendChild(link);
      }

      ret["--font-family"] = partner.font_family;
    }
    return ret;
  }, [partner]);

  useEffect(() => {
    if (
      sdkConfig.withIntercom &&
      !intercom.isOpen &&
      ![AuthState.LOADING, AuthState.INTEGRATION_ERROR].includes(authState)
    )
      intercom.show();
  }, [sdkConfig.withIntercom, authState]);

  if (authState === AuthState.LOADING) return <NoMatch />;
  if (authState === AuthState.INTEGRATION_ERROR || !token)
    return (
      <IntegrationErrorView
        errorMessage={token ? undefined : "Integration token is missing"}
      />
    );

  return (
    <div style={theme}>
      <Routes>
        <Route
          path="/plaid-connect"
          element={
            <Suspense fallback={null}>
              <PlaidConnectView />
            </Suspense>
          }
        />
        <Route path="/hub" element={<HubView />} />
        <Route path="/credit-monitoring" element={<CreditMonitoringView />} />
        <Route path="/issues/upload-lease" element={<div>Upload Lease</div>} />
        <Route
          path="/rent_reporting/onboarding/:view"
          element={
            <Suspense fallback={null}>
              <RentReportingOnboardingView />
            </Suspense>
          }
        />
        <Route
          path="/rent_reporting/issues/verify-rent"
          element={
            <Suspense fallback={null}>
              <RentReportingIssuesVerifyRentView />
            </Suspense>
          }
        />
        <Route
          path="/rent_reporting/:id/issues/m-2-m/lease-end"
          element={
            <Suspense fallback={null}>
              <RentReportingIssuesM2MLeaseEndView />
            </Suspense>
          }
        />
        <Route
          path="/rent_reporting/:id"
          element={
            <Suspense fallback={null}>
              <RentReportingId />
            </Suspense>
          }
        />
        <Route
          path="/rent_reporting/:id/move_out"
          element={
            <Suspense fallback={null}>
              <MoveOutFlow />
            </Suspense>
          }
        />
        <Route
          path="/settings"
          element={
            <Suspense fallback={null}>
              <SettingsView />
            </Suspense>
          }
        />
        <Route
          path="/settings/linked-accounts"
          element={
            <Suspense fallback={null}>
              <SettingsLinkedAccountsView />
            </Suspense>
          }
        />
        <Route
          path="/settings/delete-account"
          element={
            <Suspense fallback={null}>
              <SettingsDeleteAccountView />
            </Suspense>
          }
        />
        <Route
          path="/_dev-logout"
          element={
            <Suspense fallback={null}>
              <DevManualLogout />
            </Suspense>
          }
        />
        <Route path="/" element={<Navigate to="/hub" />} />
        <Route path="*" element={<NoMatch />} />
      </Routes>
    </div>
  );
};
